import { Injectable, inject, NgZone } from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import { filter } from "rxjs";
import { AuthService } from "./auth.service";
import { SessionExpiryService } from "./session-expiry.service";

/**
 * Detects cleared / missing client session (DevTools "Clear site data",
 * deleted localStorage, etc.) and sends the user back to /welcome without
 * leaving the admin shell in a crashing half-state.
 */
@Injectable({ providedIn: "root" })
export class SessionIntegrityService {
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);
  private readonly sessionExpiry = inject(SessionExpiryService);
  private readonly zone = inject(NgZone);

  private redirecting = false;
  private started = false;
  private pollId: ReturnType<typeof setInterval> | null = null;

  /** Call once at app start. */
  start(): void {
    if (this.started || typeof window === "undefined") return;
    this.started = true;

    const runCheck = () => this.zone.run(() => this.ensureSessionOrWelcome());

    window.addEventListener("storage", runCheck);
    window.addEventListener("focus", runCheck);
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") runCheck();
    });

    // Same-tab Application → Clear storage does not always fire `storage`.
    this.pollId = setInterval(runCheck, 2500);

    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(() => runCheck());

    // Initial check (e.g. hard refresh after clearing data).
    runCheck();
  }

  /**
   * If the current URL is protected and the client session is gone,
   * clear leftovers and navigate to welcome.
   */
  ensureSessionOrWelcome(): void {
    if (this.redirecting) return;

    const url = (this.router.url || "").split(/[?#]/)[0];
    if (!this.isProtectedUrl(url)) return;
    if (this.auth.hasUsableClientSession()) return;

    this.redirecting = true;
    try {
      this.auth.clearClientAuthStateWithoutServerCall();
      this.sessionExpiry.reset();
    } catch {
      /* ignore */
    }

    void this.router.navigateByUrl("/welcome", { replaceUrl: true }).finally(() => {
      // Allow future checks after navigation settles.
      setTimeout(() => {
        this.redirecting = false;
      }, 500);
    });
  }

  private isProtectedUrl(url: string): boolean {
    // Public auth pages must NOT match shell prefixes (e.g. /admin-login
    // starts with "/admin", /nurse-login starts with "/nurse").
    if (
      url === "/welcome" ||
      url.startsWith("/welcome/") ||
      url === "/admin-login" ||
      url.startsWith("/admin-login/") ||
      url === "/nurse-login" ||
      url.startsWith("/nurse-login/") ||
      url.startsWith("/forgot-password") ||
      url.startsWith("/reset-password") ||
      url === "/access-denied" ||
      url.startsWith("/access-denied/")
    ) {
      return false;
    }

    return (
      url === "/admin" ||
      url.startsWith("/admin/") ||
      url === "/nurse" ||
      url.startsWith("/nurse/") ||
      url === "/client" ||
      url.startsWith("/client/") ||
      url.startsWith("/invoice") ||
      url.startsWith("/walk-in-invoice")
    );
  }
}
